import React, { useState, useRef } from "react";
import { Upload } from "lucide-react";
import { memoryService, ImportResult } from "../services/memory.service";
import { useToast } from "../context/ToastContext";
import "../styles/clients.css";

interface Props {
    clientId: string;
    onClose: () => void;
    onImported: () => void;
}

export default function ImportKnowledgeModal({ clientId, onClose, onImported }: Props) {
    const { showToast } = useToast();
    const [file, setFile] = useState<File | null>(null);
    const [isPublic, setIsPublic] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState<ImportResult | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) {
            setError("Please select a file.");
            return;
        }

        setError("");
        setLoading(true);

        try {
            const ext = file.name.split(".").pop()?.toLowerCase();
            let res: ImportResult;
            if (ext === "csv") {
                res = await memoryService.importCSV(clientId, file, isPublic || undefined);
            } else {
                res = await memoryService.importExcel(clientId, file, isPublic || undefined);
            }
            setResult(res);
            showToast(`Imported ${res.successful} of ${res.total} entries.`);
            onImported();
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to import file.");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError("");
        if (fileRef.current) fileRef.current.value = "";
    };

    if (result) {
        const hasErrors = result.failed > 0;
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                    <h2>Import Results</h2>
                    <p className="modal-subtitle">File processed successfully</p>

                    <div className={`import-result ${hasErrors ? "has-errors" : "success"}`}>
                        <p><strong>Total entries:</strong> {result.total}</p>
                        <p><strong>Successful:</strong> {result.successful}</p>
                        <p><strong>Failed:</strong> {result.failed}</p>
                        {result.errors && result.errors.length > 0 && (
                            <div className="import-errors-list">
                                {result.errors.map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-modal-cancel" onClick={onClose}>
                            Close
                        </button>
                        <button type="button" className="btn-modal-submit" onClick={handleReset}>
                            Import More
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <h2>Import Knowledge</h2>
                <p className="modal-subtitle">Upload an Excel or CSV file with Q&A entries</p>

                <form className="modal-form" onSubmit={handleSubmit}>
                    {error && <div className="modal-error">{error}</div>}

                    <div className="modal-field">
                        <label htmlFor="kb-import-file">File *</label>
                        <input
                            ref={fileRef}
                            id="kb-import-file"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                    </div>

                    <div className="modal-checkbox">
                        <input
                            id="kb-import-public"
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                        />
                        <label htmlFor="kb-import-public">Make all imported entries visible to all clients</label>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-modal-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-modal-submit" disabled={loading}>
                            {loading ? (
                                <>Importing...</>
                            ) : (
                                <>
                                    <Upload size={14} />
                                    Import
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
